// Storyboard API client — XHR-based (window.fetch is monkey-patched by the
// platform script and breaks res.json(); see ProductionModule.js note).
const API = process.env.REACT_APP_BACKEND_URL;

export function xhrJson(method, url, body, headers = {}, timeoutMs = 240000) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.timeout = timeoutMs;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { data = { detail: xhr.responseText }; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: { detail: 'Request timed out' } });
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

function xhrUpload(url, formData, headers = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.timeout = 60000;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { data = { detail: xhr.responseText }; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: { detail: 'Upload timed out' } });
    xhr.send(formData);
  });
}

export function xhrBlob(url) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.timeout = 120000;
    xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300 ? xhr.response : null);
    xhr.onerror = () => resolve(null);
    xhr.ontimeout = () => resolve(null);
    xhr.send();
  });
}

export const storyboardApi = {
  list: (h) => xhrJson('GET', `${API}/api/storyboard?_=${Date.now()}`, null, h),
  create: (body, h) => xhrJson('POST', `${API}/api/storyboard`, body, h),
  get: (id, h) => xhrJson('GET', `${API}/api/storyboard/${id}?_=${Date.now()}`, null, h),
  update: (id, body, h) => xhrJson('PATCH', `${API}/api/storyboard/${id}`, body, h),
  remove: (id, h) => xhrJson('DELETE', `${API}/api/storyboard/${id}`, null, h),
  concept: (id, h) => xhrJson('POST', `${API}/api/storyboard/${id}/concept`, null, h),
  chat: (id, message, h) => xhrJson('POST', `${API}/api/storyboard/${id}/chat`, { message }, h),
  approve: (id, h) => xhrJson('POST', `${API}/api/storyboard/${id}/approve`, null, h),
  generateScenes: (id, numScenes, h) =>
    xhrJson('POST', `${API}/api/storyboard/${id}/generate-scenes`, numScenes ? { num_scenes: numScenes } : {}, h),
  pollScenes: (id, h) => xhrJson('GET', `${API}/api/storyboard/${id}/scenes?_=${Date.now()}`, null, h),
  regenerateScene: (id, sceneId, h) =>
    xhrJson('POST', `${API}/api/storyboard/${id}/scenes/${sceneId}/regenerate`, null, h),
  campaignPackage: (id, h) => xhrJson('POST', `${API}/api/storyboard/${id}/campaign-package`, null, h),
  uploadImage: (file, h) => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload(`${API}/api/storage/upload-unlinked`, fd, h);
  },
  signedUrl: (bucket, storagePath) =>
    xhrJson('POST', `${API}/api/generate/signed-url`, { bucket, storage_path: storagePath }),
};

export function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  }
  return Promise.resolve(fallbackCopy(text));
}

function fallbackCopy(text) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } catch { /* noop */ }
  ta.remove();
}

export const STATUS_META = {
  briefing:   { label: 'Briefing',   cls: 'bg-gray-100 text-gray-600' },
  concepting: { label: 'Thinking',   cls: 'bg-blue-50 text-blue-600 animate-pulse' },
  ideating:   { label: 'Refining',   cls: 'bg-blue-50 text-blue-600' },
  ready:      { label: 'Approved',   cls: 'bg-green-50 text-green-700' },
  generating: { label: 'Generating', cls: 'bg-amber-50 text-amber-700 animate-pulse' },
  complete:   { label: 'Complete',   cls: 'bg-emerald-50 text-emerald-700' },
  failed:     { label: 'Failed',     cls: 'bg-red-50 text-red-600' },
};
